import {
  BarrierLegParams,
  ObservationFrequency,
  PrimaryEventLegParams,
  ScheduleGenerator,
  ScheduleLeg,
  Strike,
} from '@/pages/instruments/new-instrument/schedule-builders/types'
import {
  _applyCouponSchedule,
  _getObservationEvents,
} from '@/pages/instruments/new-instrument/schedule-builders/utils'

export type PhoenixNonMemoryOptions = {
  strikes: Strike[]

  initial_observation_date: Date
  maturity_date: Date

  currency_id: number

  settlement_cycle: number

  autocall_yearly_observations: ObservationFrequency
  coupon_yearly_observations: ObservationFrequency

  coupon_rate_annual: number

  autocall_barrier_level: number

  payoff_barrier_level: number

  payoff_barrier_step: number | null

  autocall_barrier_step: number | null

  knockdown_input_level: number | null
}

const buildPrimaryLeg = (params: PrimaryEventLegParams<PhoenixNonMemoryOptions>): ScheduleLeg => {
  const {
    autocall_barrier_step,
    autocall_barrier_level,
    index,
    payoff_barrier_step,
    payoff_barrier_level,
    coupon_yearly_observations,
    currency_id,
    strike,
    event,
  } = params

  const effectiveAutocallBarrierStep = autocall_barrier_step !== null ? autocall_barrier_step : 0
  const autocallBarrierLevel = autocall_barrier_level + effectiveAutocallBarrierStep * index

  const effectivePayoffBarrierStep = payoff_barrier_step !== null ? payoff_barrier_step : 0
  const payoffBarrierLevel = payoff_barrier_level + effectivePayoffBarrierStep * index

  return {
    leg_id: index + 1,
    option_type: 'Barrier Digital',
    barrier_type: 'Up-and-In',
    contract_type: 'Digital',
    region_type: 'European',
    currency_id,
    strike: strike.value,
    strike_percent: strike.percent,
    coupon_rate: event.couponRate
      ? event.couponRate / coupon_yearly_observations
      : event.couponRate,
    instrument_id: strike.underlying_id,
    pay_date: event.payDate,
    start_observation_date: event.date,
    end_observation_date: event.date,
    initial_observation_date: params.initial_observation_date,
    participation_rate: 1,
    autocall_barrier_level: event.isAutocall ? autocallBarrierLevel : null,
    payoff_barrier_level: event.isCoupon ? payoffBarrierLevel : null,
  }
}

const buildBarrierLeg = (params: BarrierLegParams<PhoenixNonMemoryOptions>): ScheduleLeg => {
  const { currency_id, strike, final_leg, knockdown_input_level } = params

  return {
    leg_id: final_leg.leg_id! + 1,
    option_type: 'Barrier Vanilla',
    barrier_type: 'Down-and-In',
    contract_type: 'Put',
    region_type: 'European',
    currency_id,
    strike: strike.value,
    strike_percent: strike.percent,
    pay_date: final_leg.pay_date,
    start_observation_date: final_leg.start_observation_date,
    end_observation_date: final_leg.end_observation_date,
    initial_observation_date: final_leg.initial_observation_date,
    instrument_id: strike.underlying_id,
    autocall_barrier_level: knockdown_input_level,
    payoff_barrier_level: knockdown_input_level,
    participation_rate: -1,
    coupon_rate: null,
  }
}

export const PhoenixNonMemoryGenerator: ScheduleGenerator<PhoenixNonMemoryOptions> = {
  generate({ strikes, ...params }) {
    const legs: ScheduleLeg[] = []

    const observationEvents = _getObservationEvents(params)

    const eventsWithCoupons = _applyCouponSchedule({
      events: observationEvents,
      ...params,
    })

    for (const strike of strikes) {
      eventsWithCoupons.forEach((event, index) => {
        legs.push(buildPrimaryLeg({ strike, event, index, ...params }))
      })

      if (params.knockdown_input_level !== null) {
        const final_leg = legs[legs.length - 1]
        legs.push(buildBarrierLeg({ strike, final_leg, ...params }))
      }
    }

    return legs
  },
}
